import { Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { LoginUserModel } from '../../../models/loginUserModel';
import { DevicesService } from '../../../services/devices.service';
import { UsersService } from '../../../services/users.service';
import { LoaderComponent } from '../../../utils/loader/loader.component';
import { CommonModule } from '@angular/common';
import { GroupModel } from '../../../models/GroupModel';
import { forkJoin } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { ThemeService } from '../../../theme.service';
import { ToastService } from '../../../services/toast.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuModule } from 'primeng/menu';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-groups',
  imports: [
    LoaderComponent, ReactiveFormsModule, FormsModule, CommonModule,
    TableModule, ButtonModule, InputTextModule, DialogModule,
    IconFieldModule, InputIconModule, CheckboxModule, MenuModule,
    NgxMaskDirective, SelectButtonModule, TooltipModule
  ],
  templateUrl: './groups.component.html',
  styleUrl: './groups.component.scss',
  providers: [provideNgxMask()]
})
export class GroupsComponent {
  loading = true;
  messageLoader = 'Cargando Grupos';
  deviceGroups: any[] = [];
  devicesGroup: any[] = [];
  tempData: any[] = [];
  formDeviceGroup!: FormGroup;
  isEditing = false;
  showFormDialog = false;
  showDeleteConfirm = false;
  deleteTarget: any = null;
  assign = false;
  nameGroup = '';
  isChecked = false;
  dataResult: any[] = [];
  selectedGroup: any;
  data!: LoginUserModel;
  searchText = '';

  get isAdmin() { return this.data.UserType === 'Administrator' || this.data.UserType === 'Modify'; }

  constructor(
    private service: DevicesService,
    private userService: UsersService,
    private titleService: Title,
    private themeService: ThemeService,
    private toastSrv: ToastService
  ) {
    this.titleService.setTitle('Grupos de Dispositivos');
    this.themeService.setTitle('Grupos de Dispositivos');
    this.data = JSON.parse(localStorage.getItem('data')!);
    this.formDeviceGroup = new FormGroup({
      groupName: new FormControl('', [Validators.required, this.validateUniqueName(0)])
    });
  }

  ngOnInit() { this.loadGroups(); }

  get groupName() { return this.formDeviceGroup.get('groupName') as FormControl; }

  // --- Group CRUD ---

  openNewGroup() {
    this.isEditing = false;
    this.selectedGroup = null;
    this.groupName.clearValidators();
    this.groupName.addValidators([Validators.required, this.validateUniqueName(0)]);
    this.groupName.setValue('');
    this.showFormDialog = true;
  }

  openEditGroup(item: any) {
    this.isEditing = true;
    this.selectedGroup = item;
    this.groupName.clearValidators();
    this.groupName.addValidators([Validators.required, this.validateUniqueName(item.groupId)]);
    this.groupName.setValue(item.description);
    this.showFormDialog = true;
  }

  addGroup() {
    if (this.formDeviceGroup.invalid) return;
    const info: GroupModel = {
      clientId: this.selectedGroup ? this.selectedGroup.clientId : this.data.Client_id,
      groupId: this.selectedGroup ? this.selectedGroup.groupId : 0,
      description: this.groupName.value
    };
    this.loading = true;
    this.messageLoader = 'Guardando';
    this.service.saveGroup(info, this.data.UserType === 'Administrator' ? 'Administrador de la cuenta' : this.data.Name).subscribe({
      next: (resp: any) => {
        this.loading = false;
        this.showFormDialog = false;
        if (resp.response === 1) {
          this.toastSrv.add({ type: 'success', message: this.isEditing ? 'Grupo actualizado' : 'Grupo creado exitosamente' });
          this.loadGroups();
        } else {
          this.toastSrv.add({ type: 'error', message: 'Hubo un problema al guardar el grupo' });
        }
      },
      error: () => {
        this.loading = false;
        this.showFormDialog = false;
        this.toastSrv.add({ type: 'error', message: 'Hubo un problema al guardar el grupo' });
      }
    });
  }

  deleteGroup(item: any) {
    this.loading = true;
    this.messageLoader = 'Eliminando';
    const info: GroupModel = { clientId: this.data.Client_number, groupId: item.groupId, description: item.description };
    this.service.deleteGroup(info, this.data.UserType === 'Administrator' ? 'Administrador de la cuenta' : this.data.Name).subscribe({
      next: (resp: any) => {
        this.loading = false;
        if (resp.response === 1) {
          this.toastSrv.add({ type: 'success', message: item.description + ' eliminado' });
          this.loadGroups();
        } else {
          this.toastSrv.add({ type: 'error', message: 'No se pudo eliminar el grupo' });
        }
      },
      error: () => { this.loading = false; this.toastSrv.add({ type: 'error', message: 'Error al eliminar' }); }
    });
  }

  confirmDelete(item: any) {
    this.deleteTarget = item;
    this.showDeleteConfirm = true;
  }

  loadGroups() {
    this.loading = true;
    this.service.getGroups(this.data.Client_id).subscribe({
      next: (resp: any) => {
        this.deviceGroups = (resp.response || []).map((g: any) => ({ ...g, deviceCount: 0 }));
        this.loading = false;
        this.deviceGroups.forEach((g: any) => {
          this.service.getGroupCards(this.data.Client_id, g.groupId).subscribe({
            next: (r: any) => { g.deviceCount = r.response?.assignedList?.items?.length || 0; }
          });
        });
      },
      error: (err: any) => { this.loading = false; this.toastSrv.add({ type: 'error', message: 'Error al cargar grupos' }); }
    });
  }

  // --- Assignment ---

  openAssignment(item: any) {
    this.nameGroup = item.description;
    this.selectedGroup = item;
    this.getGroupCards(item.clientId, item.groupId);
    this.assign = true;
  }

  getGroupCards(clientId: number, groupId: number) {
    this.dataResult = [];
    this.messageLoader = 'Cargando dispositivos';
    this.loading = true;
    this.service.getGroupCards(clientId, groupId).subscribe({
      next: (resp: any) => {
        const assigned = resp.response.assignedList.items.map((i: any) => ({
          cardId: i.cardId, description: i.description, number: i.number.toString().padStart(5, '0'),
          name: i.name, plates: i.plates, groupId, type: 1
        }));
        const available = resp.response.availableList.map((i: any) => ({
          ...i, number: i.number.toString().padStart(5, '0'), groupId, type: 0
        }));
        this.isChecked = available.length === 0;
        this.devicesGroup = [...available, ...assigned].sort((a, b) => a.number.localeCompare(b.number));
        this.tempData = [...this.devicesGroup];
        this.loading = false;
      },
      error: () => { this.loading = false; this.toastSrv.add({ type: 'error', message: 'Error al cargar dispositivos' }); }
    });
  }

  select(item: any) {
    const map: any = { 0: 2, 1: 4, 2: 0, 4: 1 };
    item.type = map[item.type] ?? item.type;
    this.updateResult();
  }

  selectAll() {
    this.isChecked = !this.isChecked;
    this.devicesGroup.forEach(d => {
      if (this.isChecked && d.type === 0) d.type = 2;
      else if (this.isChecked && d.type === 4) d.type = 1;
      else if (!this.isChecked && d.type === 2) d.type = 0;
      else if (!this.isChecked && d.type === 1) d.type = 4;
    });
    this.updateResult();
  }

  private updateResult() {
    this.dataResult = this.devicesGroup.filter(d => d.type === 2 || d.type === 4);
  }

  searchByText(event: any) {
    const q = event.target.value.toLowerCase();
    this.devicesGroup = this.tempData.filter(item =>
      Object.values(item).some(v => v != null && v.toString().toLowerCase().includes(q))
    );
  }

  cancelAssignment() {
    this.devicesGroup.forEach(d => { if (d.type === 4) d.type = 1; else if (d.type === 2) d.type = 0; });
    this.dataResult = [];
    this.isChecked = false;
  }

  saveAssignment() {
    const toSave = this.dataResult.filter(d => d.type === 2);
    const toDelete = this.dataResult.filter(d => d.type === 4);
    const userName = this.data.UserType === 'Administrator' ? 'Administrador de la cuenta' : this.data.Name;

    if (toSave.length > 0) {
      this.loading = true;
      const saveData = toSave.map(i => ({
        clientNumber: this.data.Client_number, groupId: i.groupId, cardId: i.cardId,
        nameUser: userName, clientId: this.data.Client_id, description: i.number
      }));
      this.service.saveDevicesGroup(saveData).subscribe({
        next: (resp: any) => {
          if (resp.message === '') this.getGroupCards(saveData[0].clientId, saveData[0].groupId);
          else this.toastSrv.add({ type: 'error', message: resp.message });
        },
        error: () => { this.loading = false; this.toastSrv.add({ type: 'error', message: 'Error al asignar' }); }
      });
    }

    if (toDelete.length > 0) {
      this.loading = true;
      const deleteData = toDelete.map(i => ({
        clientId: this.data.Client_id, groupId: i.groupId, cardId: i.cardId, nameUser: userName, number: i.number
      }));
      forkJoin(deleteData.map(d => this.service.deleteDevicesGroup(d))).subscribe({
        next: (responses: any[]) => {
          const ok = responses.every(r => r.message === '');
          if (ok) this.getGroupCards(deleteData[0].clientId, deleteData[0].groupId);
          else this.toastSrv.add({ type: 'error', message: 'Error al desasignar algunos dispositivos' });
        },
        error: () => { this.loading = false; this.toastSrv.add({ type: 'error', message: 'Error al desasignar' }); }
      });
    }

    if (toSave.length > 0 || toDelete.length > 0) {
      this.toastSrv.add({ type: 'success', message: 'Cambios guardados' });
    }
  }

  backToGroups() { this.assign = false; this.dataResult = []; this.isChecked = false; }

  // --- Bulk Params ---

  showParamsDialog = false;
  formParams!: FormGroup;
  paramsGroup: any = null;
  paramsLoadType = true; // true = litros, false = importe
  savingParams = false;

  openBulkParams(group: any) {
    this.paramsGroup = group;
    this.paramsLoadType = true;
    this.formParams = new FormGroup({
      dailyLiters: new FormControl(0, [Validators.required, Validators.min(1)]),
      weeklyLiters: new FormControl(0, [Validators.required, Validators.min(1)]),
      monthlyLiters: new FormControl(0, [Validators.required, Validators.min(1)]),
      dailyAmount: new FormControl(0, [Validators.required, Validators.min(1)]),
      weeklyAmount: new FormControl(0, [Validators.required, Validators.min(0)]),
      monthlyAmount: new FormControl(0, [Validators.required, Validators.min(1)]),
      maxLoads: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(99)]),
    });
    this.showParamsDialog = true;
  }

  saveBulkParams() {
    if (this.formParams.invalid) return;
    this.savingParams = true;
    const f = this.formParams.value;
    this.service.getGroupCards(this.data.Client_id, this.paramsGroup.groupId).subscribe({
      next: (resp: any) => {
        const cards = resp.response?.assignedList?.items || [];
        if (cards.length === 0) {
          this.savingParams = false;
          this.toastSrv.add({ type: 'info', message: 'No hay dispositivos asignados a este grupo' });
          return;
        }
        const userName = this.data.UserType === 'Administrator' ? 'Administrador de la cuenta' : this.data.Name;
        const requests = cards.map((card: any) => {
          const device: any = {
            cardId: card.cardId,
            portalLoadType: this.paramsLoadType,
            dailyLimitLiters: f.dailyLiters,
            weeklyLimitLiters: f.weeklyLiters,
            monthlyLimitLiters: f.monthlyLiters,
            dailyLimitAmount: f.dailyAmount,
            weeklyLimitAmount: f.weeklyAmount,
            monthlyLimitAmount: f.monthlyAmount,
            maxDailyLoads: f.maxLoads,
          };
          return this.service.saveCardParams(device, this.data.Client_number, this.data.User_id, 'Parametros', userName);
        });
        forkJoin(requests).subscribe({
          next: () => {
            this.savingParams = false;
            this.showParamsDialog = false;
            this.toastSrv.add({ type: 'success', message: `Parámetros aplicados a ${cards.length} dispositivo(s)` });
          },
          error: () => {
            this.savingParams = false;
            this.toastSrv.add({ type: 'error', message: 'Error al aplicar parámetros' });
          }
        });
      },
      error: () => { this.savingParams = false; this.toastSrv.add({ type: 'error', message: 'Error al cargar dispositivos del grupo' }); }
    });
  }

  // --- Bulk Schedule ---

  showScheduleDialog = false;
  scheduleGroup: any = null;
  savingSchedule = false;
  scheduleEnabled = true;
  days = [
    { label: 'Lun', key: 'monday', hours: this.defaultHours() },
    { label: 'Mar', key: 'tuesday', hours: this.defaultHours() },
    { label: 'Mié', key: 'wednesday', hours: this.defaultHours() },
    { label: 'Jue', key: 'thursday', hours: this.defaultHours() },
    { label: 'Vie', key: 'friday', hours: this.defaultHours() },
    { label: 'Sáb', key: 'saturday', hours: this.defaultHours() },
    { label: 'Dom', key: 'sunday', hours: this.defaultHours() },
  ];

  defaultHours(): boolean[] { return Array(24).fill(true); }

  openBulkSchedule(group: any) {
    this.scheduleGroup = group;
    this.scheduleEnabled = true;
    this.days.forEach(d => d.hours = this.defaultHours());
    this.showScheduleDialog = true;
  }

  toggleHour(day: any, hour: number) { day.hours[hour] = !day.hours[hour]; }

  toggleDay(day: any) {
    const allOn = day.hours.every((h: boolean) => h);
    day.hours = Array(24).fill(!allOn);
  }

  buildScheduleString(day: any): string {
    return day.hours.map((h: boolean) => h ? '1' : '0').join('');
  }

  saveBulkSchedule() {
    this.savingSchedule = true;
    this.service.getGroupCards(this.data.Client_id, this.scheduleGroup.groupId).subscribe({
      next: (resp: any) => {
        const cards = resp.response?.assignedList?.items || [];
        if (cards.length === 0) {
          this.savingSchedule = false;
          this.toastSrv.add({ type: 'info', message: 'No hay dispositivos asignados a este grupo' });
          return;
        }
        const userName = this.data.UserType === 'Administrator' ? 'Administrador de la cuenta' : this.data.Name;
        const updateType = this.scheduleEnabled ? this.days.map(d => this.buildScheduleString(d)).join(',') : 'SinLimiteHorarios';
        const requests = cards.map((card: any) => {
          const device: any = { cardId: card.cardId };
          return this.service.saveCardParams(device, this.data.Client_number, this.data.User_id, updateType, userName);
        });
        forkJoin(requests).subscribe({
          next: () => {
            this.savingSchedule = false;
            this.showScheduleDialog = false;
            this.toastSrv.add({ type: 'success', message: `Horarios aplicados a ${cards.length} dispositivo(s)` });
          },
          error: () => {
            this.savingSchedule = false;
            this.toastSrv.add({ type: 'error', message: 'Error al aplicar horarios' });
          }
        });
      },
      error: () => { this.savingSchedule = false; this.toastSrv.add({ type: 'error', message: 'Error al cargar dispositivos del grupo' }); }
    });
  }

  validateUniqueName(groupId: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const exists = this.deviceGroups.find(g => g.description.toLowerCase() === control.value?.toLowerCase() && g.groupId !== groupId);
      return exists ? { duplicate: true } : null;
    };
  }
}
